
// Shared utility functions for status management
export const ORDER_STATUSES = {
  NEW: 'New',
  PENDING: 'Pending',
  APPOINTMENT_SCHEDULED: 'Appointment Scheduled',
  IN_PROGRESS: 'In Progress',
  READY: 'Ready',
  COMPLETED: 'Completed',
  CANCELLED: 'Cancelled',
  DELIVERED: 'Delivered'
} as const;

export const PRIORITY_LEVELS = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high'
} as const;

export const USER_ROLES = {
  CUSTOMER: 'customer',
  TAILOR: 'tailor',
  ADMIN: 'admin'
} as const;

export type OrderStatus = typeof ORDER_STATUSES[keyof typeof ORDER_STATUSES];
export type PriorityLevel = typeof PRIORITY_LEVELS[keyof typeof PRIORITY_LEVELS];
export type UserRole = typeof USER_ROLES[keyof typeof USER_ROLES];

// Status configuration for different types
export const STATUS_CONFIGS = {
  order: {
    [ORDER_STATUSES.NEW]: { 
      color: 'blue', 
      text: 'New', 
      icon: 'clock',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      borderColor: 'border-blue-200'
    },
    [ORDER_STATUSES.PENDING]: { 
      color: 'yellow', 
      text: 'Pending', 
      icon: 'clock',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
      borderColor: 'border-yellow-200'
    },
    [ORDER_STATUSES.APPOINTMENT_SCHEDULED]: { 
      color: 'purple', 
      text: 'Appointment Scheduled', 
      icon: 'calendar',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-800',
      borderColor: 'border-purple-200'
    },
    [ORDER_STATUSES.IN_PROGRESS]: { 
      color: 'blue', 
      text: 'In Progress', 
      icon: 'alert-circle',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      borderColor: 'border-blue-200'
    },
    [ORDER_STATUSES.READY]: { 
      color: 'green', 
      text: 'Ready', 
      icon: 'check-circle',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      borderColor: 'border-green-200'
    },
    [ORDER_STATUSES.COMPLETED]: { 
      color: 'gray', 
      text: 'Completed', 
      icon: 'check-circle',
      bgColor: 'bg-gray-100',
      textColor: 'text-gray-800',
      borderColor: 'border-gray-200'
    },
    [ORDER_STATUSES.CANCELLED]: { 
      color: 'red', 
      text: 'Cancelled', 
      icon: 'x-circle',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
      borderColor: 'border-red-200'
    },
    [ORDER_STATUSES.DELIVERED]: { 
      color: 'purple', 
      text: 'Delivered', 
      icon: 'check-circle',
      bgColor: 'bg-purple-100',
      textColor: 'text-purple-800',
      borderColor: 'border-purple-200'
    }
  },
  priority: {
    [PRIORITY_LEVELS.LOW]: { 
      color: 'green', 
      text: 'Low Priority',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      borderColor: 'border-green-200'
    },
    [PRIORITY_LEVELS.MEDIUM]: { 
      color: 'yellow', 
      text: 'Medium Priority',
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-800',
      borderColor: 'border-yellow-200'
    },
    [PRIORITY_LEVELS.HIGH]: { 
      color: 'red', 
      text: 'High Priority',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
      borderColor: 'border-red-200'
    }
  },
  user: {
    active: { 
      color: 'green', 
      text: 'Active',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      borderColor: 'border-green-200'
    },
    inactive: { 
      color: 'red', 
      text: 'Inactive',
      bgColor: 'bg-red-100',
      textColor: 'text-red-800',
      borderColor: 'border-red-200'
    }
  }
};

export const getStatusConfig = (status: string, type: 'order' | 'priority' | 'user' = 'order') => {
  const configs = STATUS_CONFIGS[type];
  
  // Check for exact match with backend status strings
  if (configs[status as keyof typeof configs]) {
    return configs[status as keyof typeof configs];
  }
  
  // Fallback to NEW status if no match found
  return configs[ORDER_STATUSES.NEW];
};

export const getStatusColor = (status: string, type: 'order' | 'priority' | 'user' = 'order') => {
  const config = getStatusConfig(status, type);
  return `${config.bgColor} ${config.textColor} ${config.borderColor}`;
};

export const getStatusText = (status: string, type: 'order' | 'priority' | 'user' = 'order') => {
  const config = getStatusConfig(status, type);
  return config.text;
};

export const getStatusIcon = (status: string, type: 'order' | 'priority' | 'user' = 'order') => {
  const config = getStatusConfig(status, type);
  return config.icon;
};
